import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SectionHeader from "@/components/SectionHeader";
import { MapPin, Calendar, Users, ChevronDown, ChevronUp, Bike } from "lucide-react";

function AttendeesList({ eventId, count }) {
  const [open, setOpen] = useState(false);
  const [attendees, setAttendees] = useState(null); // null = not loaded yet

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && attendees === null) {
      try {
        const r = await api.get(`/events/${eventId}/attendees`);
        setAttendees(r.data || []);
      } catch {
        setAttendees([]);
      }
    }
  };

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-1.5 text-xs font-nav uppercase tracking-widest hover:text-[var(--sr-fire)] transition-colors"
        style={{ color: "var(--sr-cream-soft)" }}
        data-testid={`toggle-attendees-${eventId}`}
      >
        <Users size={14} className="text-[var(--sr-fire)]" />
        {count || 0} jadących
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="mt-3 pl-5 border-l-2" style={{ borderColor: "var(--sr-fire)" }} data-testid={`attendees-${eventId}`}>
          {attendees === null ? (
            <p className="text-xs" style={{ color: "var(--sr-muted)" }}>Ładowanie...</p>
          ) : attendees.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--sr-muted)" }}>Nikt jeszcze się nie zapisał.</p>
          ) : (
            <ul className="space-y-1.5">
              {attendees.map((a) => (
                <li key={a.id} className="text-sm" style={{ color: "var(--sr-cream-soft)" }}>
                  <span className="font-display text-[var(--sr-cream)]">{a.name}</span>
                  {a.motorcycle && (
                    <span className="text-xs ml-2 inline-flex items-center gap-1" style={{ color: "var(--sr-fire-2)" }}>
                      <Bike size={11} /> {a.motorcycle}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const { user } = useAuth();

  const load = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleAttend = async (id) => {
    try {
      await api.post(`/events/${id}/attend`);
      load();
    } catch (e) { /* no-op */ }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="events-page">
      <SectionHeader title="Wydarzenia" />
      <p className="text-center mt-4 mb-10" style={{ color: "var(--sr-cream-soft)" }}>
        Zloty, kawalkady, charytatywne przejazdy. Bądź w drodze.
      </p>

      <div className="space-y-5">
        {events.map((ev) => {
          const isAttending = ev.attendees?.includes(user?.id);
          return (
            <article key={ev.id} className="sr-card p-6 md:p-8 flex flex-col md:flex-row gap-6" data-testid={`event-${ev.id}`}>
              {ev.image_url && (
                <div className="md:w-72 flex-shrink-0 gallery-img" style={{ aspectRatio: "16/10" }}>
                  <img src={ev.image_url} alt={ev.title} />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-display text-2xl text-[var(--sr-fire-2)]">{ev.title}</h3>
                <div className="flex flex-wrap gap-4 mt-2 font-nav uppercase tracking-widest text-xs" style={{ color: "var(--sr-cream-soft)" }}>
                  <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[var(--sr-fire)]" /> {new Date(ev.date).toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" })}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[var(--sr-fire)]" /> {ev.location}</span>
                </div>
                <p className="mt-4" style={{ color: "var(--sr-cream-soft)" }}>{ev.description}</p>
                {user && <AttendeesList eventId={ev.id} count={ev.attendees?.length || 0} />}
                {!user && (
                  <p className="mt-4 text-xs font-nav uppercase tracking-widest flex items-center gap-1.5" style={{ color: "var(--sr-muted)" }}>
                    <Users size={14} className="text-[var(--sr-fire)]" /> {ev.attendees?.length || 0} jadących
                  </p>
                )}
                <button
                  className={isAttending ? "btn-solid mt-5" : "btn-fire mt-5"}
                  onClick={() => toggleAttend(ev.id)}
                  data-testid={`attend-${ev.id}`}
                  disabled={!user}
                  style={!user ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                  title={!user ? "Zaloguj się, aby się zapisać" : ""}
                >
                  {user ? (isAttending ? "Jadę! (Anuluj)" : "Zapisuję się") : "Zaloguj się, aby zapisać"}
                </button>
              </div>
            </article>
          );
        })}
        {events.length === 0 && <p className="text-center" style={{ color: "var(--sr-muted)" }}>Brak nadchodzących wydarzeń.</p>}
      </div>
    </div>
  );
}
